import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faWrench,
  faBriefcase,
  faIndustry,
  faPeopleRoof,
  faShieldAlt,
  faFileContract,
  faFire,
  faGavel,
  faAmbulance,
  faHome,
  faPlane,
  faDog,
  faShip,
  faLock,
  faTruck,
  faUserInjured,
  faVirus,
  faCogs,
  faFlask,
  faBalanceScale,
  faBriefcaseMedical,
  faLaptopCode,
  faChalkboardTeacher,
  faPiggyBank,
  faSuitcaseRolling,
  faSmog,
  faPeopleRobbery,
  faHeartPulse,
  faHospital
} from '@fortawesome/free-solid-svg-icons';
import { motion, AnimatePresence } from 'framer-motion';
import './Policies.css';
import SEO from "../components/SEO";

function Policies() {
  // 🔹 Artigiani aperto di default
  const [activeSection, setActiveSection] = useState('artigiani');
  const location = useLocation();

  // 🔹 Riferimenti (usati solo per scroll iniziale)
  const artigianiRef = useRef(null);
  const professionistiRef = useRef(null);
  const industrieRef = useRef(null);
  const privatiRef = useRef(null);

  const toggleSection = (sectionId) => {
    setActiveSection((prev) => (prev === sectionId ? null : sectionId));
  };

useEffect(() => {
  const hash = location.hash.substring(1);
  const stateHash = location.state?.hash;
  const targetHash = hash || stateHash;

  if (targetHash && ['artigiani', 'professionisti', 'industrie', 'privati'].includes(targetHash)) {
    setActiveSection(targetHash);

    // Attendi che la sezione sia renderizzata da Framer Motion
    const interval = setInterval(() => {
      const refMap = {
        artigiani: artigianiRef,
        professionisti: professionistiRef,
        industrie: industrieRef,
        privati: privatiRef,
      };

      const targetRef = refMap[targetHash]?.current;
      if (targetRef) {
        clearInterval(interval);

        // Calcola posizione della sezione
        const elementRect = targetRef.getBoundingClientRect();
        const absoluteTop = elementRect.top + window.scrollY;

        // Altezza header dinamica
        const header = document.querySelector('header');
        const headerHeight = header ? header.offsetHeight : 0;

        // Scroll invisibile (prima che appaia la dissolvenza)
        window.scrollTo({
          top: absoluteTop - headerHeight - 60,
          behavior: 'auto',
        });
      }
    }, 100);

    return () => clearInterval(interval);
  } else {
    setActiveSection('artigiani');
  }
}, [location]);

  const sectionVariants = {
    hidden: { opacity: 0, y: 25 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: "easeOut" },
    },
    exit: { opacity: 0, y: -20, transition: { duration: 0.4, ease: "easeInOut" } },
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.4 }}
    >
      <SEO
        title="Polizze Assicurative | Andolfatto Insurance Broker"
        description="Scopri le polizze assicurative su misura di Andolfatto Insurance Broker: soluzioni per artigiani, professionisti, industrie e privati. Proteggi ciò che conta con coperture personalizzate e assistenza continua."
        url="https://andolfattobroker.it/polizze-assicurative"
      />

      <div className="policies-page">
        {/* INTRO */}
        <section className="policies-intro">
          <h1>Polizze Assicurative</h1>
          {/* 🔽 H2 nascosti SEO ottimizzati */}
          <h2 className="visually-hidden">
            Polizze assicurative per artigiani, professionisti, industrie e privati a Bassano del Grappa.
          </h2>

          <h2 className="visually-hidden">
            Soluzioni assicurative personalizzate per proteggere persone, attività e patrimoni.
          </h2>

          <h2 className="visually-hidden">
            Consulenza e assistenza continua per ogni tipo di copertura assicurativa.
          </h2>

          <h2 className="visually-hidden">
            Responsabilità civile, danni, furti, salute, vita e tutela legale — tutte le polizze in un unico partner affidabile.
          </h2>

          <h2 className="visually-hidden">
            Andolfatto Insurance Broker: esperienza e indipendenza al servizio della tua sicurezza.
          </h2>
          <p>
            Scopri le nostre polizze assicurative su misura per artigiani, professionisti, industrie e privati.
          </p>
          <p>Ogni categoria ha soluzioni specifiche per proteggere la tua attività e i tuoi beni.</p>
        </section>

        {/* MENÙ CATEGORIE */}
        <nav className="policies-nav">
          <ul>
            <li>
              <button
                className={activeSection === 'artigiani' ? 'active' : ''}
                onClick={() => toggleSection('artigiani')}
              >
                <FontAwesomeIcon icon={faWrench} /> Artigiani
              </button>
            </li>
            <li>
              <button
                className={activeSection === 'professionisti' ? 'active' : ''}
                onClick={() => toggleSection('professionisti')}
              >
                <FontAwesomeIcon icon={faBriefcase} /> Professionisti
              </button>
            </li>
            <li>
              <button
                className={activeSection === 'industrie' ? 'active' : ''}
                onClick={() => toggleSection('industrie')}
              >
                <FontAwesomeIcon icon={faIndustry} /> Industrie
              </button>
            </li>
            <li>
              <button
                className={activeSection === 'privati' ? 'active' : ''}
                onClick={() => toggleSection('privati')}
              >
                <FontAwesomeIcon icon={faPeopleRoof} /> Privati
              </button>
            </li>
          </ul>
        </nav>

        <div className="policy-sections">
          <AnimatePresence mode="wait">
            {activeSection === 'artigiani' && (
              <motion.section
                key="artigiani"
                id="artigiani"
                className="policy-section"
                ref={artigianiRef}
                variants={sectionVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
              >
                {/* Contenuto della sezione Artigiani */}
                <div className="section-header">
                  <FontAwesomeIcon icon={faWrench} className="section-icon" />
                  <h2>Proteggi ogni aspetto del tuo lavoro</h2>
                </div>
                <p className="section-intro">
                  Che tu sia falegname, idraulico, elettricista, meccanico, sarto o muratore, abbiamo la polizza su misura per te.
                  Scegli tra le diverse tipologie di coperture e crea la protezione più adatta alla tua attività.
                </p>

                <div className="policies-grid">
                  {[
                    { icon: faShieldAlt, title: "Responsabilità Civile Professionale (RC)", desc: "Protegge da richieste di risarcimento per danni involontari a persone, cose o animali durante lo svolgimento del lavoro." },
                    { icon: faFire, title: "Danni a Locali e Attrezzature", desc: "Copertura contro incendio, esplosione, allagamento, eventi atmosferici e atti vandalici che colpiscono il laboratorio, il magazzino o il negozio." },
                    { icon: faLock, title: "Furto e Rapina", desc: "Indennizzo per furto o rapina di strumenti, macchinari, materiali o prodotti finiti, sia nei locali di lavoro che durante il trasporto." },
                    { icon: faCogs, title: "Macchinari e Attrezzature", desc: "Tutela per guasti accidentali o danni a utensili e macchinari indispensabili all’attività, compresi quelli di nuova generazione." },
                    { icon: faUserInjured, title: "Infortuni sul Lavoro", desc: "Copre l’artigiano e i collaboratori in caso di infortuni durante o a causa dell’attività lavorativa." },
                    { icon: faAmbulance, title: "Malattia e Indennità Giornaliera", desc: "Sostegno economico in caso di malattia che impedisca di lavorare, con diaria giornaliera per compensare la perdita di reddito." },
                    { icon: faFileContract, title: "Interruzione Attività", desc: "Rimborsa le perdite economiche dovute alla sospensione forzata dell’attività per eventi come incendio, alluvione o danni strutturali." },
                    { icon: faGavel, title: "Tutela Legale", desc: "Copertura delle spese legali e peritali in caso di contenziosi legati all’attività." },
                    { icon: faTruck, title: "Trasporto Merci e Attrezzature", desc: "Protezione durante il trasporto di strumenti, prodotti o materiali verso cantieri, fiere o clienti." },
                    { icon: faVirus, title: "Cyber per Artigiani", desc: "Tutela contro rischi digitali come attacchi informatici, furto dati o blocco dei sistemi di gestione." },
                    { icon: faFlask, title: "RC Prodotti", desc: "Copertura per danni causati da beni o prodotti realizzati e venduti." }
                  ].map((policy, index) => (
                    <motion.div
                      key={index}
                      className="policy-card"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05, duration: 0.4, ease: "easeOut" }}
                      viewport={{ once: true, amount: 0.2 }}
                    >
                      <FontAwesomeIcon icon={policy.icon} className="policy-icon" />
                      <h3>{policy.title}</h3>
                      <p>{policy.desc}</p>
                    </motion.div>
                  ))}
                </div> {/* ✅ chiude la .policies-grid */}

              </motion.section>
            )}
            {activeSection === 'professionisti' && (
              <motion.section
                key="professionisti"
                id="professionisti"
                className="policy-section"
                ref={professionistiRef}
                variants={sectionVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
              >
              <div className="section-header">
                <FontAwesomeIcon icon={faBriefcase} className="section-icon" />
                <h2>Coperture assicurative su misura per ogni professione</h2>
              </div>
              <p className="section-intro">
                Ogni professionista, indipendentemente dal settore, affronta responsabilità e rischi che possono avere conseguenze economiche e legali.
                Le coperture assicurative professionali permettono di tutelare il proprio lavoro e la propria reputazione.
              </p>
              <div className="categories-list">
                <h3>Categorie di Professionisti</h3>
                <ul>
                  <li><FontAwesomeIcon icon={faBriefcaseMedical} /> Sanità e Medicina</li>
                  <li><FontAwesomeIcon icon={faCogs} /> Tecnici e Ingegneri</li>
                  <li><FontAwesomeIcon icon={faBalanceScale} /> Professioni Legali e Amministrative</li>
                  <li><FontAwesomeIcon icon={faLaptopCode} /> Settore IT e Digitale</li>
                  <li><FontAwesomeIcon icon={faChalkboardTeacher} /> Settore Educativo e Formativo</li>
                  <li><FontAwesomeIcon icon={faPiggyBank} /> Settore Finanziario e Assicurativo</li>
                  <li><FontAwesomeIcon icon={faSuitcaseRolling} /> Altri professionisti</li>
                </ul>
              </div>
              <div className="policies-grid">
                {[
                  { icon: faShieldAlt, title: "RC Professionale", desc: "Tutela contro richieste di risarcimento per danni patrimoniali, materiali o personali causati nell’esercizio della professione." },
                  { icon: faGavel, title: "Tutela Legale", desc: "Copertura delle spese legali in caso di contenzioso." },
                  { icon: faUserInjured, title: "Infortuni e Malattia", desc: "Indennizzi in caso di inabilità temporanea o permanente in convenzione." }
                ].map((policy, index) => (
                <motion.div
                  key={index}
                  className="policy-card"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05, duration: 0.4, ease: "easeOut" }}
                  viewport={{ once: true, amount: 0.2 }}
                >
                    <FontAwesomeIcon icon={policy.icon} className="policy-icon" />
                    <h3>{policy.title}</h3>
                    <p>{policy.desc}</p>
                  </motion.div>
                ))}
              </div>
            </motion.section>
          )}

          {activeSection === 'industrie' && (
              <motion.section
                key="professionisti"
                id="professionisti"
                className="policy-section"
                ref={professionistiRef}
                variants={sectionVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
              >
              <div className="section-header">
                <FontAwesomeIcon icon={faIndustry} className="section-icon" />
                <h2>Copertura assicurativa per il settore industriale</h2>
              </div>
              <p className="section-intro">
                Le industrie operano in contesti ad alto rischio, dove un singolo imprevisto può avere conseguenze significative su produzione, bilanci e reputazione.
                Una protezione assicurativa mirata permette di gestire al meglio i rischi, garantendo continuità operativa e sicurezza del capitale investito.
              </p>
              <div className="policies-grid">
                {[
                  { icon: faFire, title: "Incendio e Danni ai Beni", desc: "Protezione per edifici industriali, impianti, linee produttive, magazzini e merci contro incendi, esplosioni, eventi atmosferici, allagamenti e atti vandalici." },
                  { icon: faShieldAlt, title: "RC Generale (Responsabilità Civile verso Terzi)", desc: "Tutela contro richieste di risarcimento per danni a persone o cose derivanti dall’attività industriale." },
                  { icon: faFlask, title: "RC Prodotti", desc: "Copertura per danni causati da prodotti difettosi, anche dopo la distribuzione o l’esportazione." },
                  { icon: faSmog, title: "RC Inquinamento", desc: "Protezione contro danni ambientali accidentali (sversamenti, contaminazioni, emissioni nocive)." },
                  { icon: faCogs, title: "Guasti Macchinari", desc: "Indennizzo per danni accidentali a macchinari, impianti di produzione e attrezzature tecnologiche." },
                  { icon: faFileContract, title: "Interruzione dell’Attività", desc: "Risarcimento per le perdite economiche causate da fermi produttivi dovuti a eventi imprevisti." },
                  { icon: faLock, title: "Furto e Rapina", desc: "Copertura per sottrazione di macchinari, componenti, materie prime e prodotti finiti." },
                  { icon: faTruck, title: "Trasporto Merci", desc: "Protezione durante la spedizione di materie prime e prodotti finiti, sia su territorio nazionale che internazionale." },
                  { icon: faUserInjured, title: "Infortuni sul Lavoro", desc: "Copertura integrativa per titolari e dipendenti, anche oltre quanto previsto per legge." },
                  { icon: faGavel, title: "Tutela Legale", desc: "Copertura delle spese legali in caso di vertenze civili, penali o amministrative." },
                  { icon: faVirus, title: "Cyber Risk Industriale", desc: "Protezione contro attacchi informatici che colpiscono sistemi gestionali, logistici o di automazione industriale." }
                ].map((policy, index) => (
                  <motion.div
                  key={index}
                  className="policy-card"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05, duration: 0.4, ease: "easeOut" }}
                  viewport={{ once: true, amount: 0.2 }}
                  >
                    <FontAwesomeIcon icon={policy.icon} className="policy-icon" />
                    <h3>{policy.title}</h3>
                    <p>{policy.desc}</p>
                  </motion.div>
                ))}
              </div>
            </motion.section>
          )}

          {activeSection === 'privati' && (
              <motion.section
                key="professionisti"
                id="professionisti"
                className="policy-section"
                ref={professionistiRef}
                variants={sectionVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
              >
              <div className="section-header">
                <FontAwesomeIcon icon={faPeopleRoof} className="section-icon" />
                <h2>La protezione che ti segue nella vita di tutti i giorni</h2>
              </div>
              <p className="section-intro">
                Dalla casa alla salute, dal tempo libero alla tutela legale, le polizze per privati permettono di proteggere ciò che conta di più:
                la famiglia, i beni e la serenità personale.
              </p>
              <div className="policies-grid">
                {[
                  { icon: faHome, title: "Assicurazione Casa", desc: "Protezione per l’abitazione e il contenuto da incendio, esplosioni, eventi atmosferici, allagamenti e furti." },
                  { icon: faShieldAlt, title: "Responsabilità Civile del Capofamiglia", desc: "Copre danni involontari causati a terzi da tutti i membri della famiglia, inclusi animali domestici." },
                  { icon: faPeopleRobbery, title: "Polizza Furto", desc: "Indennizzo per furto, rapina o scippo, anche fuori dall’abitazione." },
                  { icon: faHospital, title: "Assicurazione Salute", desc: "Rimborso spese mediche per ricoveri, interventi chirurgici, visite specialistiche e terapie." },
                  { icon: faUserInjured, title: "Polizza Infortuni", desc: "Copertura in caso di infortuni domestici, stradali, sportivi o durante il tempo libero." },
                  { icon: faHeartPulse, title: "Polizza Vita", desc: "Protezione economica per i familiari in caso di decesso o invalidità grave." },
                  { icon: faGavel, title: "Tutela Legale", desc: "Copertura delle spese legali in caso di controversie civili o penali." },
                  { icon: faPlane, title: "Polizza Viaggio", desc: "Assistenza e indennizzo per imprevisti durante viaggi in Italia o all’estero (cancellazioni, bagagli smarriti, spese mediche)." },
                  { icon: faDog, title: "Assicurazione Animali Domestici", desc: "Copre spese veterinarie e responsabilità civile per danni causati dall’animale." },
                  { icon: faShip, title: "Assicurazione Nautica", desc: "RC e coperture danni per imbarcazioni da diporto." }
                ].map((policy, index) => (
                  <motion.div
                  key={index}
                  className="policy-card"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05, duration: 0.4, ease: "easeOut" }}
                  viewport={{ once: true, amount: 0.2 }}
                  >
                    <FontAwesomeIcon icon={policy.icon} className="policy-icon" />
                    <h3>{policy.title}</h3>
                    <p>{policy.desc}</p>
                  </motion.div>
                ))}
              </div>
            </motion.section>
          )}
        </AnimatePresence>
      </div>
      </div>
    </motion.div>
  );
}

export default Policies;
